import { IRuntimeBridgeClient } from '../../serviceworkerBridgeClients/IRuntimeBridgeClient';

/**
 * Service that provides a unified interface for runtime operations,
 * abstracting the differences between native and PWA environments.
 * 
 * Handles:
 * - Application lifecycle (quit/restart)
 * - Screen recording
 * - File uploads
 * - Language settings
 * - Online status
 */
class JsBridgeRuntimeService {
    /**
     * @param {SignalsService} $signals - Service for event handling.
     * @param {LogBridgeService} LogBridgeService - Logging service.
     * @param {Window} $window - Window object.
     * @param {angular.IQService} $q - Angular promise service.
     * @param {angular.ITimeoutService} $timeout - Angular timeout service.
     * @param {IRuntimeBridgeClient} RuntimeBridgeClient - Runtime bridge implementation.
     */
    constructor($signals, LogBridgeService, $window, $q, $timeout, RuntimeBridgeClient, ScreenRecordingBridgeClient) {
        this.$signals = $signals;
        this.$logger = LogBridgeService;
        this.$window = $window;
        this.$q = $q;
        this.$timeout = $timeout;
        this.RuntimeBridgeClient = RuntimeBridgeClient;

        this._logIdentifier = 'ConfigService';
        this.$logger.info(this, 'initialize');
    }

    /**
     * Quits the application.
     * @param {boolean} restart - Whether to restart the application.
     * @param {boolean} openFileExplorer - Whether to open the file explorer.
     */
    quit(restart = false, openFileExplorer = false) {
        this.$logger.info(this, `quit(${restart ? 'restart' : 'no-restart'} ${openFileExplorer ? 'openFileExplorer' : 'no-openFileExplorer'})`);
        return this.RuntimeBridgeClient.quit(restart, openFileExplorer);
    }

    /**
     * Notifies that the frontend is ready.
     */
    frontendReady() {
        this.$logger.info(this, 'frontendReady()');
        return this.RuntimeBridgeClient.frontendReady();
    }

    /**
     * Uploads a response file.
     * @param {string} targetFilename - The target filename.
     * @param {string} apiUrl - The API URL to use for the response upload.
     * @param {Blob} fileData - The file data to upload.
     * @param {boolean} [deleteTemporaryResponseDataOnSuccess] - Whether to delete temporary response data store after successful upload.
     * @param {number} [recoveryEntryId] - Optional PendingRecoveryEntry ID to delete on upload success.
     * @returns {Promise} - A promise that resolves when the upload is successful.
     */
    uploadResponse(targetFilename, apiUrl, fileData, deleteTemporaryResponseDataOnSuccess, recoveryEntryId) {
        const deferred = this.$q.defer();

        // Clean up any previous signal handlers still attached from a prior call
        // (e.g. if that call's progress handler never saw a terminal signal).
        if (this._uploadSuccessHandler) this.$signals.remove('uploadSuccess', this._uploadSuccessHandler);
        if (this._uploadFailedHandler) this.$signals.remove('uploadFailed', this._uploadFailedHandler);
        if (this._uploadProgressHandler) this.$signals.remove('uploadProgress', this._uploadProgressHandler);

        const cleanup = () => {
            this.$signals.remove('uploadSuccess', this._uploadSuccessHandler);
            this.$signals.remove('uploadFailed', this._uploadFailedHandler);
            this.$signals.remove('uploadProgress', this._uploadProgressHandler);
            this._uploadSuccessHandler = null;
            this._uploadFailedHandler = null;
            this._uploadProgressHandler = null;
        };

        this._uploadSuccessHandler = () => { cleanup(); deferred.resolve(); };
        this._uploadFailedHandler = () => { cleanup(); deferred.reject(); };
        this._uploadProgressHandler = (progress, message) => {
            deferred.notify({ progress, message });
        };

        this.$signals.on('uploadSuccess', this._uploadSuccessHandler);
        this.$signals.on('uploadFailed', this._uploadFailedHandler);
        this.$signals.on('uploadProgress', this._uploadProgressHandler);

        this.RuntimeBridgeClient.uploadResponse(targetFilename, apiUrl, fileData, deleteTemporaryResponseDataOnSuccess, recoveryEntryId);

        return deferred.promise;
    }

    /**
     * Checks if the application is online.
     * @returns {boolean} - True if the application is online, false otherwise.
     */
    isOnline() {
        return this.RuntimeBridgeClient.isOnline();
    }

    /**
     * Checks if the application is running on Windows.
     * @returns {boolean} - True if the application is running on Windows, false otherwise.
     */
    isWindows() {
        return this.RuntimeBridgeClient.isWindows();
    }

    /**
     * Gets the media URL.
     * @returns {string} - The media URL.
     */
    getMediaUrl() {
        return this.RuntimeBridgeClient.getMediaUrl();
    }

    /**
     * Changes the keyboard language.
     * @param {string} value - The new keyboard language.
     * @returns {boolean} - False if the language is not changed, true otherwise.
     */
    changeKeyboardLanguage(value) {
        if (this.lastKeyboardLanguage === value) return false;
        if (!value && this.lastKeyboardLanguage) {
            value = this.lastKeyboardLanguage;
        }
        if (!value) return false;

        this.RuntimeBridgeClient.changeKeyboardLanguage(value);
        console.log('changeKeyboardLanguage: ' + value);
        this.lastKeyboardLanguage = value;
        return false;
    }

    /**
     * Lists the available keyboard languages.
     * @param {boolean} [allowCache=true] - Whether to allow caching of the result.
     * @returns {Array} - The list of available keyboard languages.
     */
    listKeyboardLanguagesAvailable(allowCache = true) {
        if (allowCache && this._keyboardLanguagesCache) {
            return this._keyboardLanguagesCache;
        }

        const l = this.RuntimeBridgeClient.listKeyboardLanguagesAvailable();
        if (typeof l === 'string') {
            this._keyboardLanguagesCache = JSON.parse(l);
        } else {
            this._keyboardLanguagesCache = l;
        }

        return this._keyboardLanguagesCache;
    }

    /**
     * Sets the runtime language.
     * @param {string} value - The new runtime language.
     */
    setRuntimeLanguage(value) {
        this.RuntimeBridgeClient.setRuntimeLanguage(value);
        console.log('setRuntimeLanguage: ' + value);
    }

    /**
     * Sets the last keyboard language.
     * @returns {boolean} - False if the language is not changed, true otherwise.
     */
    setLastKeyboardLanguage() {
        if (!this.allowChangeKeyboardLanguage) return false;
        const availableLanguages = this.listKeyboardLanguagesAvailable();
        if (Array.isArray(availableLanguages) && availableLanguages.length > 1) {
            this.allowChangeKeyboardLanguage = false;
            setTimeout(() => {
                this.allowChangeKeyboardLanguage = true;
            }, 50);
            this.$timeout(() => {
                this.changeKeyboardLanguage();
            });
        }
        return false;
    }
}

JsBridgeRuntimeService.$inject = ['$signals', 'LogBridgeService', '$window', '$q', '$timeout', 'RuntimeBridgeClient'];

export default JsBridgeRuntimeService;
